import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./OpenShutterPaywall.module.scss"

/**
 * OpenShutterPaywall (`/openshutter`) — a self-contained NON-ONBOARDING upsell for
 * Private Photo Vault, built around a single photographic idea: the LONG EXPOSURE.
 *
 * A long exposure keeps the shutter open so every point of light that passes is
 * gathered into one continuous, luminous trail. That is the promise of the yearly
 * plan, made literal: **keep the shutter open on your whole year** and every new
 * memory is gathered, private and safe, into one unbroken frame. A monthly plan is
 * a single frame — you re-open the shutter every month.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/open-shutter.md
 *
 * Chosen so every axis lands where the ledger is NOT saturated:
 *   - Layout:      a dark EXPOSURE VIEWFINDER with a horizontal light-trail TRACK,
 *                  an EV/seconds exposure meter, and a shutter release. No centered
 *                  hero, no glowing single card, no grid, no receipt, no calendar.
 *   - Interaction: HOLD-TO-EXPOSE. Holding the shutter INTEGRATES light over TIME —
 *                  the trail accumulates the longer you hold and gathers each moment
 *                  it sweeps past. A time-integrated exposure, not a position scrub,
 *                  a reveal, a toggle, or a page-turn.
 *   - Register:    luminous WONDER / flow / momentum — "a whole year, gathered into
 *                  one unbroken light." Not warm-ivory nostalgia, not fear/urgency,
 *                  not luxury-status, not deadpan.
 *   - Art:        DARK long-exposure NIGHT PHOTOGRAPHY — the 4th dark paywall ever,
 *                  and the first light-painting one; deliberately divergent from the
 *                  ~45 light/warm-ivory paywalls. Uses the app's OWN dark-theme
 *                  tokens (#1d212b ground, #406ff6 primary) so it is on-brand. Brand
 *                  blue is the gathered light; amber (--color-secondary) is the warm
 *                  spark of each moment. Real bundled photo thumbnails fuse into the
 *                  trail so it reads as a photo VAULT within three seconds.
 *   - Value:      CONTINUITY / peace-of-mind — "keep every new memory gathered, all
 *                  year, automatically." A commitment + best-value frame for yearly,
 *                  framed positively (what you gather going forward), never as loss.
 *
 * Honest & App Store compliant: every claim maps to what a private photo/video
 * vault with automatic backup actually does (private storage, automatic backup of
 * new photos & videos, everything in one place). The on-screen exposure seconds and
 * "moments" are an explicitly-labelled SAMPLE year, not a claim about the viewer's
 * own library. Nothing implies data is lost when a plan ends — copy says the
 * opposite, plainly. No countdown, no fake scarcity; the savings figure is exact
 * yearly-vs-12×monthly arithmetic. Yearly is the default, featured plan with the
 * most prominent price; monthly is present but small and secondary.
 */

type PlanKey = "yearly" | "monthly"

type Moment = {
  id: string
  src: string
  at: number // position along the year, 0..1
  tone: string // warm fallback colour if the image is slow to load
}

// Fourteen real, warm, candid "a year of a life" photos already bundled in the
// repo (public/byheart/*.jpg — no network egress). Spread across the year so the
// exposure sweeps past them one at a time and fuses each into the trail.
const RAW: Array<[string, string]> = [
  ["m01", "#3b628f"],
  ["m03", "#8f5b3b"],
  ["m05", "#c8783e"],
  ["m07", "#3f6f6a"],
  ["m09", "#a9713f"],
  ["m11", "#83506b"],
  ["m12", "#4a6f43"],
  ["m13", "#b06a44"],
  ["m14", "#55617f"],
  ["m16", "#7d5a3c"],
  ["m17", "#4f7d8a"],
  ["m18", "#9a5c58"],
  ["m10", "#c98f4b"],
  ["m08", "#476084"],
]

const MOMENTS: Moment[] = RAW.map(([id, tone], i) => ({
  id,
  src: `/byheart/${id}.jpg`,
  // nudge positions off a perfect grid so the sweep feels captured, not metered
  at: (i + 0.5) / RAW.length + (i % 3 === 0 ? 0.012 : -0.01),
  tone,
}))

const MONTHS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"]

// sample readouts for the demo year (clearly labelled on screen)
const FULL_SECONDS = 4.2 // the metaphorical shutter time of a full-year exposure
const FULL_MOMENTS = 1240 // sample count of moments gathered across the year

const clamp = (v: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, v))

// ── inline glyphs (no new asset deps) ────────────────────────────────────────
const Aperture = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <circle
      cx={12}
      cy={12}
      r={9}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
    />
    {[0, 60, 120, 180, 240, 300].map((a) => {
      const r = (a * Math.PI) / 180
      const x1 = 12 + 9 * Math.cos(r)
      const y1 = 12 + 9 * Math.sin(r)
      const x2 = 12 + 3 * Math.cos(r + 0.9)
      const y2 = 12 + 3 * Math.sin(r + 0.9)
      return (
        <line
          key={a}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="currentColor"
          strokeWidth={1.3}
          strokeLinecap="round"
        />
      )
    })}
  </svg>
)

const Tick = ({ size = 11 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// eases a displayed number toward a target so meters tick rather than jump
function useTweenTo(target: number, reduce: boolean | null) {
  const [val, setVal] = useState(reduce ? target : 0)
  const raf = useRef<number | null>(null)
  const cur = useRef(reduce ? target : 0)
  useEffect(() => {
    if (reduce) {
      cur.current = target
      setVal(target)
      return
    }
    const step = () => {
      const d = target - cur.current
      if (Math.abs(d) < 0.4) {
        cur.current = target
        setVal(target)
        raf.current = null
        return
      }
      cur.current += d * 0.2
      setVal(cur.current)
      raf.current = requestAnimationFrame(step)
    }
    if (raf.current == null) raf.current = requestAnimationFrame(step)
    return () => {
      if (raf.current != null) cancelAnimationFrame(raf.current)
      raf.current = null
    }
  }, [target, reduce])
  return val
}

const OpenShutterPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── exposure state ───────────────────────────────────────────────────────
  // `exposed` (0..1) is how far across the year the shutter has gathered light.
  // It only ever climbs while held — a long exposure latches what it has caught.
  const [exposed, setExposed] = useState(0)
  const [holding, setHolding] = useState(false)
  const [hinted, setHinted] = useState(false)

  const exposedRef = useRef(0)
  const holdingRef = useRef(false)
  const raf = useRef<number | null>(null)
  const last = useRef<number>(0)
  const trackRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    exposedRef.current = exposed
  }, [exposed])

  const stopRaf = useCallback(() => {
    if (raf.current != null) {
      cancelAnimationFrame(raf.current)
      raf.current = null
    }
  }, [])
  useEffect(() => () => stopRaf(), [stopRaf])

  // the integration loop: while the shutter is open, gather light over TIME.
  const loop = useCallback(
    (now: number) => {
      const dt = last.current ? (now - last.current) / 1000 : 0
      last.current = now
      if (holdingRef.current) {
        // a full-year exposure takes ~2.6s of holding; ease near the end
        const remaining = 1 - exposedRef.current
        const rate = 0.38 + remaining * 0.22
        const next = clamp(exposedRef.current + dt * rate)
        exposedRef.current = next
        setExposed(next)
        if (next >= 1) {
          holdingRef.current = false
          setHolding(false)
          native.logMixpanelEvent("html_openshutter_full", {
            seconds: FULL_SECONDS,
          })
          stopRaf()
          return
        }
      }
      if (holdingRef.current) raf.current = requestAnimationFrame(loop)
      else stopRaf()
    },
    [stopRaf],
  )

  const beginExposure = useCallback(() => {
    if (reduceMotion) {
      exposedRef.current = 1
      setExposed(1)
      setHinted(true)
      return
    }
    if (exposedRef.current >= 1) return
    setHinted(true)
    holdingRef.current = true
    setHolding(true)
    last.current = 0
    stopRaf()
    raf.current = requestAnimationFrame(loop)
    native.logMixpanelEvent("html_openshutter_hold", {})
  }, [loop, reduceMotion, stopRaf])

  const endExposure = useCallback(() => {
    holdingRef.current = false
    setHolding(false)
  }, [])

  const reset = useCallback(() => {
    stopRaf()
    holdingRef.current = false
    setHolding(false)
    exposedRef.current = 0
    setExposed(0)
    native.logMixpanelEvent("html_openshutter_reset", {})
  }, [stopRaf])

  // teaching nudge: on load the trail gathers a sliver of light by itself, then
  // waits for the user — "hold to keep the shutter open."
  useEffect(() => {
    if (reduceMotion) {
      setExposed(1)
      exposedRef.current = 1
      setHinted(true)
      return
    }
    let cancelled = false
    const start = window.setTimeout(() => {
      const t0 = performance.now()
      const dur = 900
      const tick = (n: number) => {
        if (cancelled) return
        const k = clamp((n - t0) / dur)
        const v = 0.14 * (k < 0.7 ? k / 0.7 : 1)
        exposedRef.current = v
        setExposed(v)
        if (k < 1) requestAnimationFrame(tick)
        else setHinted(true)
      }
      requestAnimationFrame(tick)
    }, 620)
    return () => {
      cancelled = true
      window.clearTimeout(start)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── derived display ──────────────────────────────────────────────────────
  const gathered = useMemo(
    () => MOMENTS.filter((m) => exposed >= m.at - 0.001).length,
    [exposed],
  )
  const done = exposed >= 0.999
  const secondsTarget = exposed * FULL_SECONDS
  const momentsTarget = Math.round(exposed * FULL_MOMENTS)
  const seconds = useTweenTo(secondsTarget * 10, reduceMotion) / 10
  const momentsShown = Math.round(useTweenTo(momentsTarget, reduceMotion))

  const status = done
    ? "Year exposed — one unbroken frame"
    : holding
      ? "Shutter open — gathering light…"
      : hinted
        ? "Hold the shutter to gather your year"
        : "Sample year · ready"

  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? `Open the shutter — ${trialDays} days free`
        : "Open the shutter for a year"
      : "Continue month to month"
  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `then ${yearlyPrice}/year · cancel anytime`
        : `${yearlyPrice}/year · auto-renews · cancel anytime`
      : `${monthlyPrice}/month · auto-renews · cancel anytime`

  return (
    <div className={styles.page}>
      <div className={styles.grain} aria-hidden />

      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.brandKey} aria-hidden>
            <Aperture size={15} />
          </span>
          <span className={styles.brandName}>Private Photo Vault</span>
          <span className={styles.premiumTag}>Premium</span>
        </div>
        <h1 className={styles.title}>
          Keep the shutter open on your&nbsp;<em>whole year.</em>
        </h1>
        <p className={styles.sub}>
          A long exposure gathers every point of light into one unbroken trail.
          So does a year of Premium — every new photo and video, gathered
          private and safe into one continuous frame.
        </p>
      </header>

      {/* ── the exposure viewfinder — the mechanic ─────────────────────────── */}
      <section className={styles.finder} aria-label="Long-exposure preview">
        <div className={styles.finderTop}>
          <span className={styles.corner} data-c="tl" aria-hidden />
          <span className={styles.corner} data-c="tr" aria-hidden />
          <div className={styles.meterCluster}>
            <div className={styles.meter}>
              <span className={styles.meterLabel}>Exposure</span>
              <span className={styles.meterVal}>
                {seconds.toFixed(1)}
                <i>s</i>
              </span>
            </div>
            <span className={styles.meterDivider} aria-hidden />
            <div className={styles.meter}>
              <span className={styles.meterLabel}>Moments gathered</span>
              <span className={styles.meterVal}>
                {momentsShown.toLocaleString()}
              </span>
            </div>
            <span className={styles.sampleTag}>sample year</span>
          </div>
        </div>

        {/* the year track: a light-trail the shutter paints across 12 months */}
        <div
          ref={trackRef}
          className={`${styles.track} ${holding ? styles.trackLive : ""} ${
            done ? styles.trackDone : ""
          }`}
          role="button"
          tabIndex={0}
          aria-label="Hold to keep the shutter open and gather your year"
          onPointerDown={(e) => {
            e.preventDefault()
            beginExposure()
            trackRef.current?.setPointerCapture?.(e.pointerId)
          }}
          onPointerUp={endExposure}
          onPointerLeave={endExposure}
          onPointerCancel={endExposure}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              beginExposure()
            }
          }}
        >
          {/* the un-gathered rail + the gathered luminous trail over it */}
          <span className={styles.rail} aria-hidden />
          <span
            className={styles.trail}
            style={{ width: `${clamp(exposed) * 100}%` }}
            aria-hidden
          >
            <span className={styles.trailCore} />
            <span className={styles.trailHead} />
          </span>

          {/* month ticks */}
          <div className={styles.months} aria-hidden>
            {MONTHS.map((m, i) => {
              const at = (i + 0.5) / 12
              return (
                <span
                  key={i}
                  className={`${styles.month} ${
                    exposed >= at ? styles.monthLit : ""
                  }`}
                  style={{ left: `${at * 100}%` }}
                >
                  {m}
                </span>
              )
            })}
          </div>

          {/* the moments the exposure sweeps up */}
          {MOMENTS.map((m) => {
            const caught = exposed >= m.at - 0.001
            return (
              <span
                key={m.id}
                className={`${styles.moment} ${caught ? styles.momentOn : ""}`}
                style={{
                  left: `${m.at * 100}%`,
                  ["--tone" as string]: m.tone,
                }}
                aria-hidden
              >
                <span className={styles.momentDot} />
                <span className={styles.momentThumb}>
                  <img src={m.src} alt="" draggable={false} loading="lazy" />
                </span>
              </span>
            )
          })}
        </div>

        <div className={styles.finderBottom}>
          <span className={styles.corner} data-c="bl" aria-hidden />
          <span className={styles.corner} data-c="br" aria-hidden />

          <button
            className={`${styles.shutter} ${holding ? styles.shutterOpen : ""}`}
            onPointerDown={(e) => {
              e.preventDefault()
              beginExposure()
            }}
            onPointerUp={endExposure}
            onPointerLeave={endExposure}
            onPointerCancel={endExposure}
            aria-label="Hold to open the shutter"
          >
            <span className={styles.shutterRing} aria-hidden>
              <Aperture size={22} />
            </span>
            <span className={styles.shutterText}>
              {done
                ? "Exposure locked"
                : holding
                  ? "Gathering…"
                  : "Hold to expose"}
            </span>
          </button>

          <div className={styles.statusRow}>
            <span
              className={`${styles.statusDot} ${
                holding
                  ? styles.statusDotLive
                  : done
                    ? styles.statusDotDone
                    : ""
              }`}
              aria-hidden
            />
            <span className={styles.status} aria-live="polite">
              {status}
            </span>
            {done && (
              <button className={styles.replay} onClick={reset}>
                Rewind
              </button>
            )}
          </div>
        </div>
      </section>

      {/* honest, camera-caption feature notes (not an icon-tile perk grid) */}
      <ul className={styles.notes}>
        {[
          "Everything private, in one vault — photos and videos, locked to you",
          "Automatic backup gathers every new memory as you go, all year",
          "Your whole year stays organised and always in reach",
        ].map((t) => (
          <li key={t}>
            <span className={styles.notesTick} aria-hidden>
              <Tick size={9} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* ── exposure settings = pricing · yearly featured ──────────────────── */}
      <div className={styles.plans}>
        <span className={styles.plansLabel}>Choose your exposure</span>

        <button
          className={`${styles.planYear} ${
            plan === "yearly" ? styles.planSel : ""
          }`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planYearTop}>
            <span className={styles.planMode}>
              <span className={styles.planModeName}>Long exposure</span>
              <span className={styles.planModeSub}>
                The whole year — one unbroken frame
              </span>
            </span>
            {savingsPct > 0 && (
              <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
            )}
          </span>
          <span className={styles.planPriceRow}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/year</span>
            <span className={styles.planMo}>≈ {yearlyPerMonth}/mo</span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.planMonthSel : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthLeft}>
            <span className={styles.planMonthName}>Single frame</span>
            <span className={styles.planMonthSub}>
              Monthly — re-open each month
            </span>
          </span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaGlow} aria-hidden />
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Your photos are always yours and always safe in your vault. Premium
        simply keeps every new memory gathered and protected, automatically.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default OpenShutterPaywall
